import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import type { TUseRequestUpdaterProps } from 'features/request/useRequest';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { UerSaveOrUpdateWorkOrderChangeSeasonVolumeMutation } from 'gql/graphql';

import type { GridApi } from 'ag-grid-community';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { saveOrUpdateWorkOrderChangeSeasonVolumeGql } from '../WorkOrderChangeQueries';

interface IUseUpdateWorkOrderChangePlanVolume {
    refreshTable?: () => void;
    useAgCellRowDataRefresh?: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams?: any;
    api?: GridApi;
    workOrderChangeRootObjectId?: number;
}

const requestId = 'saveOrUpdateWorkOrderChangeSeasonVolume';

export const useUpdateWorkOrderChangePlanVolume = (props: IUseUpdateWorkOrderChangePlanVolume) => {
    const dispatch = useDispatch();

    const { refreshTable, useAgCellRowDataRefresh, useAgCellRowDataRefreshParams, api, workOrderChangeRootObjectId } =
        props || {};

    const [workOrderChangeSeasonVolumeReset] = useResetStoreData({ requestId });

    const { reloadHandler } =
        useAgCellRowDataRefresh?.({
            requestParams: useAgCellRowDataRefreshParams,
            api,
        }) || {};

    const onSuccess = useCallback(
        (depsVars: TUseRequestUpdaterProps<UerSaveOrUpdateWorkOrderChangeSeasonVolumeMutation>) => {
            let data = depsVars?.payload?.uerSaveOrUpdateWorkOrderChangeSeasonVolume?.data || [];

            if (workOrderChangeRootObjectId === null) {
                api?.refreshInfiniteCache();
            } else {
                data = [...data, workOrderChangeRootObjectId];
            }

            if (refreshTable) {
                refreshTable();
            } else if (workOrderChangeRootObjectId !== null) {
                reloadHandler?.(data);
            }

            workOrderChangeSeasonVolumeReset(undefined);
        },
        [api, refreshTable, reloadHandler, workOrderChangeRootObjectId, workOrderChangeSeasonVolumeReset],
    );

    const [, , , workOrderChangeSeasonVolumeRequest] = useRequest({
        requestId,
        query: saveOrUpdateWorkOrderChangeSeasonVolumeGql,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const handleUpdateVolume = useCallback(
        (variablesVolume, externalUpdater?: () => void) => {
            dispatch(
                workOrderChangeSeasonVolumeRequest(variablesVolume, (deps) => {
                    onSuccess(deps);
                    externalUpdater?.();
                }),
            );
        },
        [dispatch, onSuccess, workOrderChangeSeasonVolumeRequest],
    );

    return {
        handleUpdateVolume,
    };
};
